import customTargetingClient from './customTargetingClient';
import {
    AutoCleanupState,
    CreateKeyInput,
    CreateValueInput,
    CustomTargetingKey,
    CustomTargetingValue,
    KeyCapacityResponse,
    KeyCleanupInput,
    KeyCleanupJob,
    KeyCleanupPreview,
    MessageResponse,
    PagedValuesResponse,
    ValuesQuery
} from "@/types/custom-targeting";
import { getSelectedNetwork } from '@/lib/gamNetwork';

// Keys são por conta GAM: a mesma key (utm_term) existe nas duas contas com ids
// diferentes. Values e jobs de limpeza pendem de um key_id, que já é de uma
// conta só; por isso só as chamadas por nome ou de listagem levam a conta.
const comConta = (params = new URLSearchParams()): URLSearchParams => {
    const network = getSelectedNetwork();
    if (network) params.append('network_code', network);
    return params;
};
const qs = (params: URLSearchParams) => (params.toString() ? `?${params.toString()}` : '');

// ==================== CUSTOM TARGETING KEYS ====================

export const getCustomTargetingKeys = async (): Promise<CustomTargetingKey[]> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys${qs(comConta())}`);
    return response.data.data;
};

export const getCustomTargetingKey = async (keyId: string): Promise<CustomTargetingKey> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys/${keyId}`);
    return response.data;
};

export const createCustomTargetingKey = async (data: CreateKeyInput): Promise<CustomTargetingKey> => {
    const network = getSelectedNetwork();
    const response = await customTargetingClient.post(
        '/api/v1/custom-targeting/keys',
        network ? { ...data, network_code: network } : data
    );
    return response.data;
};

/**
 * Quanto do limite de values do GAM (~100 mil por key) cada key de UTM já usou.
 *
 * O total vem do próprio GAM e inclui os inativos, que continuam ocupando vaga.
 * O servidor guarda em cache por 15 min; `refresh` força nova consulta.
 */
export const getKeysCapacity = async (refresh = false): Promise<KeyCapacityResponse> => {
    const params = comConta();
    if (refresh) params.append('refresh', 'true');
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys-capacity${qs(params)}`);
    return response.data;
};

// ==================== LIMPEZA DE CAPACIDADE (só admin) ====================
// Desativa no GAM values antigos e sem tráfego para liberar vaga no limite de
// ativos. O backend devolve 403 para não-admin, 422 quando recusa a prévia e
// 409 quando recusa iniciar — sempre com `detail` legível.

export const getKeyCleanupPreview = async (
    name: string,
    input: KeyCleanupInput
): Promise<KeyCleanupPreview> => {
    const params = comConta(new URLSearchParams({
        quantity: String(input.quantity),
        idle_days: String(input.idle_days),
    }));
    const response = await customTargetingClient.get(
        `/api/v1/custom-targeting/keys-cleanup/${encodeURIComponent(name)}/preview?${params.toString()}`
    );
    return response.data;
};

export const startKeyCleanup = async (name: string, input: KeyCleanupInput): Promise<KeyCleanupJob> => {
    const response = await customTargetingClient.post(
        `/api/v1/custom-targeting/keys-cleanup/${encodeURIComponent(name)}${qs(comConta())}`,
        input
    );
    return response.data;
};

export const getKeyCleanupJob = async (jobId: string): Promise<KeyCleanupJob> => {
    const response = await customTargetingClient.get(
        `/api/v1/custom-targeting/keys-cleanup/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
};

// ==================== LIMPEZA AUTOMÁTICA (1x por dia) ====================
// A rodada cobre as duas contas GAM de uma vez, por isso não leva a conta.

export const getAutoCleanup = async (limit = 14): Promise<AutoCleanupState> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys-cleanup-auto?limit=${limit}`);
    return response.data;
};

/** Só admin (403 para os demais). Pausar não interrompe uma rodada em andamento. */
export const setAutoCleanupEnabled = async (enabled: boolean): Promise<AutoCleanupState> => {
    const response = await customTargetingClient.put('/api/v1/custom-targeting/keys-cleanup-auto', { enabled });
    return response.data;
};

// ==================== CUSTOM TARGETING VALUES ====================

export const getCustomTargetingValues = async (
    keyId?: string,
    statusFilter?: string
): Promise<CustomTargetingValue[]> => {
    const params = new URLSearchParams();
    if (keyId) params.append('key_id', keyId);
    if (statusFilter) params.append('status_filter', statusFilter);

    const queryString = params.toString();
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/values${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
};

/**
 * Busca uma página de values, com status e período já filtrados no servidor.
 *
 * A tela usava getCustomTargetingValues e filtrava em memória. Isso só
 * funcionava porque ninguém tinha aberto uma key grande: utm_term_hour tem
 * mais de 800 mil values, e baixar todos derrubava o worker da API.
 */
export const getPagedCustomTargetingValues = async (
    query: ValuesQuery
): Promise<PagedValuesResponse> => {
    const params = new URLSearchParams();
    params.append('key_id', query.keyId);
    params.append('page', String(query.page));
    params.append('per_page', String(query.perPage));
    if (query.status) params.append('status_filter', query.status);
    if (query.dateFrom) params.append('date_from', query.dateFrom);
    if (query.dateTo) params.append('date_to', query.dateTo);
    if (query.search) params.append('search', query.search);

    const response = await customTargetingClient.get(
        `/api/v1/custom-targeting/values?${params.toString()}`
    );
    return response.data;
};

export const getCustomTargetingValue = async (valueId: string): Promise<CustomTargetingValue> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/values/${valueId}`);
    return response.data;
};

export const createCustomTargetingValue = async (data: CreateValueInput): Promise<CustomTargetingValue> => {
    const response = await customTargetingClient.post('/api/v1/custom-targeting/values', data);
    return response.data;
};

export const deleteCustomTargetingValue = async (valueId: string): Promise<MessageResponse> => {
    const response = await customTargetingClient.delete(`/api/v1/custom-targeting/values/${valueId}`);
    return response.data;
};
